import "../../../../css/GlobalStyles.css";
import {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  accountActions,
  getAccountState,
} from "../../../../redux/slices/accountSlice";
import { MixpanelContext } from "../../../../context/AnalyticsService";
import { NavigationId } from "../../../../navigation/AppParamList";
import FullScreenContainer from "../../../Containers/FullScreenContainer";
import RectangleButton from "../../../Buttons/RectangleButton";
import { Colors } from "../../../../utils/colors";
import { Icon } from "@iconify/react";
import { AccessLevel, Event, NotificationType } from "@markit/common.types";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import {
  createEventData,
  updateEventData,
} from "../../../../redux/slices/eventSlice";
import { addToCurrUserWishList } from "../../../../redux/slices/wishMarkSlice";
import CreateProductDetails from "./CreateProductDetails";
import CreateProductPublished from "./CreateProductPublished";
import {
  useEventSelector,
  useParametricSelector,
} from "../../../../hooks/useParametricSelector";
import { getLoadedEvent } from "../../../../redux/slices/dataSlice";
import { useLocation, useNavigate } from "react-router-dom";
import isEqual from "lodash.isequal";
import ConfirmDeleteModal from "../../../Containers/ConfirmPopups/ConfirmDeleteModal";
import LoadingScreen from "../../../../screens/LoadingScreen";
import EnablePaidTicketsModal from "../../../DisplayModal/EnablePaidTicketsModal";
import {
  isEventTicketsPaid,
  isProductWithCheckout,
} from "@markit/common.utils";
import { checkIfAutomationExists } from "../../../../utils/campaignUtils";
import { getEventFormQuestions } from "../../../../utils/eventUtils/formUtils";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import { Editor } from "@tiptap/react";

enum CreateProductStage {
  CREATE = "Create",
  DETAILS = "Details",
  PUBLISHED = "Published",
}

type CreateProductWrapperProps = {
  tempProductData: Event;
  editor: Editor;
  setTempProductData: (tempProductData: Event) => void;
  createComp: ReactNode;
  onValidate: () => boolean;
  onFinalize: () => Promise<Event | undefined>; // return undefined if upload was canceled
};

// A wrapper used for product creation flows (post, video, etc)
const CreateProductWrapper = (props: CreateProductWrapperProps) => {
  const {
    tempProductData,
    editor,
    setTempProductData,
    createComp,
    onValidate,
    onFinalize,
  } = props;
  const { accountData, readyForPayment } = useSelector(getAccountState).account;
  const navigate = useNavigate();
  const { duplicatedEvent, cloneDetails } = useLocation().state || {};
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);
  const [stage, setStage] = useState<CreateProductStage>(
    CreateProductStage.CREATE
  );
  const [isSaving, setIsSaving] = useState(false);
  const [connectStripeModal, setConnectStripeModal] = useState(false);
  const [showConfirmBackModal, setShowConfirmBackModal] = useState(false);

  const styles = {
    borderButton: { padding: 14, border: `0.5px solid ${Colors.GRAY2}` },
  };

  const productId = useEventSelector();
  const effectiveProductId = duplicatedEvent ? "" : productId;
  const {
    isLoading: isLoadingProductData,
    data: productData,
    isError: isErrorProductData,
  } = useParametricSelector(getLoadedEvent, effectiveProductId);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (productData) {
      setTempProductData(productData);

      if (editor && productData.body) {
        editor.commands.setContent(productData.body);
      }

      const formQuestions = await getEventFormQuestions(productData);
      dispatch(accountActions.updateCurrentEventFormQuestions(formQuestions));
    }
  }, [dispatch, editor, productData, setTempProductData]);

  const isEditing = useMemo(
    () => effectiveProductId !== "",
    [effectiveProductId]
  );

  const madeChanges = useMemo(
    () => isEditing && !isEqual(productData, tempProductData),
    [isEditing, productData, tempProductData]
  );

  const backOnPress = useCallback(() => {
    stage === CreateProductStage.DETAILS
      ? setStage(CreateProductStage.CREATE)
      : stage === CreateProductStage.PUBLISHED || isEditing || duplicatedEvent
        ? isEditing || duplicatedEvent
          ? navigate(-1)
          : navigate(`/e/${tempProductData.id}/dashboard`, {
              state: { eventId: tempProductData.id },
            })
        : navigate(NavigationId.NEW_PRODUCT);
  }, [duplicatedEvent, isEditing, navigate, stage, tempProductData.id]);

  const continueOnPress = useCallback(async () => {
    if (!onValidate()) {
      return;
    }
    setStage(CreateProductStage.DETAILS);
  }, [onValidate]);

  const publishOnPress = useCallback(async () => {
    if (
      tempProductData.accessLevel === AccessLevel.CHECKOUT &&
      isEventTicketsPaid(tempProductData) &&
      !readyForPayment
    ) {
      // Check if access level is checkout and stripe is not connected
      setConnectStripeModal(true);
      return;
    }

    if (isEditing) {
      setStage(CreateProductStage.CREATE);
      return;
    }
    setIsSaving(true);
    const finalProduct = await onFinalize();
    if (!finalProduct) {
      setIsSaving(false);
      return;
    }
    const dispatchPromises = [
      dispatch(
        createEventData(
          finalProduct,
          mixpanel,
          isProductWithCheckout(
            finalProduct.eventType,
            finalProduct.accessLevel
          ),
          cloneDetails
        )
      ),
      dispatch(addToCurrUserWishList(finalProduct.id, accountData.uid)),
    ];
    await Promise.all(dispatchPromises);

    showNotificationBanner(
      dispatch,
      `${tempProductData.eventType} Created!`,
      NotificationType.AFFIRMATIVE
    );
    setStage(CreateProductStage.PUBLISHED);
    setIsSaving(false);
  }, [
    tempProductData,
    readyForPayment,
    isEditing,
    onFinalize,
    dispatch,
    mixpanel,
    cloneDetails,
    accountData.uid,
  ]);

  const updateProductOnPress = useCallback(async () => {
    if (!onValidate()) {
      return;
    }
    setIsSaving(true);
    const finalProduct = await onFinalize();
    if (!finalProduct) {
      setIsSaving(false);
      return;
    }

    // Check if automation exists for the checkout product, and if it needs to be created
    const automationExists = await checkIfAutomationExists(
      accountData.uid,
      finalProduct.id
    );
    const createNewAutomation =
      finalProduct.accessLevel === AccessLevel.CHECKOUT && !automationExists;

    await dispatch(updateEventData(finalProduct, createNewAutomation));
    showNotificationBanner(
      dispatch,
      `${tempProductData.eventType} Updated!`,
      NotificationType.AFFIRMATIVE
    );
    navigate(`/e/${tempProductData.id}/dashboard`, {
      state: { eventId: tempProductData.id },
    });
    setIsSaving(false);
  }, [
    onValidate,
    onFinalize,
    accountData.uid,
    dispatch,
    tempProductData.eventType,
    tempProductData.id,
    navigate,
  ]);

  const doneOnPress = useCallback(() => {
    navigate(`/e/${tempProductData.id}/dashboard`, {
      state: { eventId: tempProductData.id },
    });
  }, [navigate, tempProductData.id]);

  if (isEditing && isLoadingProductData) {
    return <LoadingScreen isLoadingPage={isLoadingProductData} />;
  }

  if (
    isEditing &&
    (isErrorProductData || (!isLoadingProductData && !productData))
  ) {
    return <LoadingScreen error />;
  }

  return (
    <FullScreenContainer
      title={
        stage === CreateProductStage.CREATE
          ? isEditing
            ? "Edit Product"
            : "Customize"
          : stage === CreateProductStage.DETAILS
            ? isEditing
              ? "Settings"
              : "Publish"
            : "Published"
      }
      helmetTitle={`${isEditing ? "Edit" : "Create"} ${
        tempProductData.eventType
      }`}
      backOnPress={() => {
        if (stage === CreateProductStage.CREATE && madeChanges) {
          setShowConfirmBackModal(true);
          return;
        }
        backOnPress();
      }}
      alternateLeftIcon="ion:arrow-back"
      rightHeaderComp={
        <div className="AlignedRow" style={{ gap: 10 }}>
          {isEditing && stage === CreateProductStage.CREATE ? (
            <RectangleButton
              buttonLabel="Settings"
              onPress={() => setStage(CreateProductStage.DETAILS)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={styles.borderButton}
              iconLeft={
                <Icon
                  icon="ion:cog"
                  height={19}
                  style={{ color: Colors.BLACK }}
                />
              }
            />
          ) : stage === CreateProductStage.PUBLISHED ? (
            <RectangleButton
              buttonLabel="New Product"
              onPress={() => navigate(NavigationId.NEW_PRODUCT)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={{
                whiteSpace: "nowrap",
                ...styles.borderButton,
              }}
            />
          ) : null}
          <div className="onboarding-sell-products-publish-target">
            <RectangleButton
              buttonLabel={
                stage === CreateProductStage.CREATE
                  ? isEditing
                    ? "Save"
                    : "Continue"
                  : stage === CreateProductStage.DETAILS
                    ? isEditing
                      ? "Done"
                      : "Publish"
                    : "Done"
              }
              onPress={
                stage === CreateProductStage.CREATE
                  ? isEditing
                    ? updateProductOnPress
                    : continueOnPress
                  : stage === CreateProductStage.DETAILS
                    ? publishOnPress
                    : doneOnPress
              }
              altColor={
                stage === CreateProductStage.DETAILS && !isEditing
                  ? Colors.BLUE5
                  : Colors.BLACK
              }
              disabled={tempProductData.title === ""}
              loading={isSaving}
              altTextColor={Colors.WHITE}
              containerStyles={{ padding: 14 }}
              iconRight={
                stage === CreateProductStage.CREATE && !isEditing ? (
                  <Icon
                    icon="ion:arrow-forward"
                    height={19}
                    style={{
                      color:
                        tempProductData.title === ""
                          ? Colors.GRAY2
                          : Colors.WHITE,
                    }}
                  />
                ) : undefined
              }
            />
          </div>
        </div>
      }
    >
      {stage === CreateProductStage.CREATE ? (
        <div
          className="ColumnNormal"
          style={{
            gap: 24,
            marginInline: "auto",
            minHeight: 700,
            width: 700,
            paddingBottom: 100,
          }}
        >
          {createComp}
        </div>
      ) : stage === CreateProductStage.DETAILS ? (
        <CreateProductDetails
          productDataSettings={tempProductData}
          updateProductDataSettings={(update: Partial<Event>) => {
            setTempProductData({ ...tempProductData, ...update });
          }}
          isEditing={isEditing}
        />
      ) : (
        <CreateProductPublished productDataSettings={tempProductData} />
      )}
      <ConfirmDeleteModal
        heading="Are you sure you want to exit?"
        subtext="Your edits won't be saved. This cannot be undone."
        deleteButtonText="Exit"
        hideModal={!showConfirmBackModal}
        setIsVisible={setShowConfirmBackModal}
        deleteOnPress={backOnPress}
        icon={<Icon icon="ion:exit-outline" height={40} />}
      />
      {connectStripeModal ? (
        <EnablePaidTicketsModal
          isVisible={connectStripeModal}
          setIsVisible={setConnectStripeModal}
        />
      ) : null}
    </FullScreenContainer>
  );
};

export default memo(CreateProductWrapper);
